import React, { useEffect, useState } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
} from 'recharts';
import { Box, Typography } from '@mui/material';
import { swotAnalysisService, SwotData } from '../../services/SwotAnalysisService';

interface FormattedSwotData {
    name: string;
    valor: number;
    color: string;
}

const SwotHistogramChart: React.FC = () => {
    const [swotData, setSwotData] = useState<SwotData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchSwotData = async () => {
            try {
                setLoading(true);
                const data = await swotAnalysisService.obterDadosSwot();
                setSwotData(data);
                setLoading(false);
            } catch (err) {
                console.error('Erro ao carregar dados SWOT para histograma:', err);
                setError('Não foi possível carregar os dados da análise SWOT');
                setLoading(false);
            }
        };

        fetchSwotData();
    }, []);

    // Formatar dados para o gráfico de histograma
    const formatDataForHistogramChart = (data: SwotData | null): FormattedSwotData[] => {
        if (!data) return [];

        return [
            { name: 'Pontos Fortes', valor: data.pontosFortesPontuacaoTotal, color: '#4caf50' }, // Verde
            { name: 'Pontos Fracos', valor: data.pontosFracosPontuacaoTotal, color: '#f44336' }, // Vermelho
            { name: 'Oportunidades', valor: data.oportunidadesPontuacaoTotal, color: '#2196f3' }, // Azul
            { name: 'Ameaças', valor: data.ameacasPontuacaoTotal, color: '#ff9800' }, // Laranja
        ];
    };

    if (loading) {
        return <Typography>Carregando histograma SWOT...</Typography>;
    }

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }

    const histogramData = formatDataForHistogramChart(swotData);

    return (
        <Box sx={{ width: '100%', height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={histogramData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                        dataKey="name"
                        tick={{ fontSize: 12 }}
                        angle={-25}
                        textAnchor="end"
                        height={60}
                    />
                    <YAxis
                        label={{
                            value: 'Pontuação',
                            angle: -90,
                            position: 'insideLeft',
                            style: { textAnchor: 'middle' }
                        }}
                    />
                    <Tooltip
                        formatter={(value) => [`${value}`, 'Pontuação']}
                        labelStyle={{ fontWeight: 'bold' }}
                    />
                    <Bar dataKey="valor" name="Pontuação">
                        {histogramData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
};

export default SwotHistogramChart;
